<template src="./FormTemplateEditor.html"></template>
<script>
import { getFormTemplate, createFormTemplate, updateFormTemplate, publishFormTemplate, duplicateFormTemplate, getTemplateServices, updateTemplateServices, getServices } from '../../../services'
import draggable from 'vuedraggable'

const FIELD_TYPES = [
  { value: 'TEXT', text: 'Texto corto', icon: 'mdi-format-text' },
  { value: 'TEXTAREA', text: 'Texto largo', icon: 'mdi-text-box-outline' },
  { value: 'NUMBER', text: 'Numero', icon: 'mdi-numeric' },
  { value: 'MONEY', text: 'Dinero', icon: 'mdi-currency-usd' },
  { value: 'DATE', text: 'Fecha', icon: 'mdi-calendar' },
  { value: 'SELECT', text: 'Seleccion', icon: 'mdi-form-dropdown' },
  { value: 'CHOICE', text: 'Opcion unica', icon: 'mdi-radiobox-marked' },
  { value: 'MULTI_CHOICE', text: 'Opcion multiple', icon: 'mdi-checkbox-marked-outline' },
  { value: 'PHOTO', text: 'Foto', icon: 'mdi-camera' },
  { value: 'FILES', text: 'Archivos', icon: 'mdi-paperclip' },
  { value: 'TABLE', text: 'Tabla', icon: 'mdi-table' },
  { value: 'LIST', text: 'Lista', icon: 'mdi-format-list-bulleted' },
]

const WIDTH_OPTIONS = [
  { value: 'full', text: '100%' },
  { value: 'half', text: '50%' },
  { value: 'third', text: '33%' },
]

const slugify = (text) => {
  let slug = text
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase()
    .substring(0, 40)
  if (slug && /^[0-9]/.test(slug)) slug = 'f_' + slug
  return slug
}

const emptyField = () => ({
  key: '', label: '', type: 'TEXT', width: 'full',
  required: false, options: [], fields: [], rows: [], _autoKey: true,
})

const emptyGroup = () => ({
  key: '', title: '', description: '', fields: [emptyField()], _autoKey: true,
})

const emptySection = () => ({
  key: '', title: '', newPage: false, reusable: false, groups: [emptyGroup()], _autoKey: true,
})

export default {
  name: 'FormTemplateEditor',
  components: { draggable },
  data: () => ({
    loading: true,
    saving: false,
    isNew: true,
    templateId: null,
    name: '',
    description: '',
    displayOrder: 0,
    version: 1,
    status: 'DRAFT',
    definition: [],
    linkedServiceIds: [],
    allServices: [],
    expandedSections: {},
    selectedField: null,
    activeView: 'structure',
    editingSection: null,
    editingGroup: null,
    FIELD_TYPES,
    WIDTH_OPTIONS,
  }),

  computed: {
    isReadonly () {
      return this.status === 'ARCHIVED' || this.status === 'PUBLISHED'
    },
    readonlyMessage () {
      if (this.status === 'ARCHIVED') return 'Plantilla archivada. Puedes republicarla o duplicarla para crear una nueva version.'
      if (this.status === 'PUBLISHED') return 'Plantilla publicada. Duplicala para hacer cambios sin afectar procesos activos.'
      return ''
    },
    canPublish () {
      return !this.isNew && this.status === 'DRAFT'
    },
    statusColor () {
      if (this.status === 'PUBLISHED') return 'green'
      if (this.status === 'ARCHIVED') return 'orange'
      return 'grey'
    },
    statusLabel () {
      if (this.status === 'PUBLISHED') return 'Publicada'
      if (this.status === 'ARCHIVED') return 'Archivada'
      return 'Borrador'
    },
    selectedFieldData () {
      if (!this.selectedField) return null
      const { sIndex, gIndex, fIndex } = this.selectedField
      return this.definition[sIndex]?.groups[gIndex]?.fields[fIndex] || null
    },
    totalGroups () {
      return this.definition.reduce((s, sec) => s + sec.groups.length, 0)
    },
    totalFields () {
      return this.definition.reduce((s, sec) => s + sec.groups.reduce((g, gr) => g + gr.fields.length, 0), 0)
    },
  },

  watch: {
    activeView () {
      this.selectedField = null
      this.editingSection = null
      this.editingGroup = null
    },
  },

  async created () {
    await this.loadServices()
    const id = this.$route.params.id
    if (id && id !== 'new') {
      this.isNew = false
      this.templateId = id
      await this.loadTemplate()
    } else {
      this.definition = [emptySection()]
      this.expandedSections = { 0: true }
      this.activeView = 'info'
      this.loading = false
    }
  },

  mounted () {
    this.adjustEditorHeight()
    window.addEventListener('resize', this.adjustEditorHeight)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.adjustEditorHeight)
  },

  methods: {
    async loadServices () {
      try { this.allServices = await getServices() } catch (e) { console.error(e) }
    },

    async loadTemplate () {
      try {
        this.loading = true
        const data = await getFormTemplate(this.templateId)
        this.name = data.name
        this.description = data.description || ''
        this.displayOrder = data.displayOrder || 0
        this.version = data.version
        this.status = data.status
        this.definition = (data.definition || []).map(s => ({
          ...s, _autoKey: false,
          groups: (s.groups || []).map(g => ({
            ...g, _autoKey: false,
            fields: (g.fields || []).map(f => ({ ...f, _autoKey: false })),
          })),
        }))
        if (!this.definition.length) this.definition = [emptySection()]
        this.expandedSections = { 0: true }
        const services = await getTemplateServices(this.templateId)
        this.linkedServiceIds = services.map(s => s.serviceId || s.id)
        if (this.status !== 'DRAFT') this.activeView = 'preview'
      } catch (err) {
        console.error(err)
        this.$swal({ text: 'No se pudo cargar la plantilla.', icon: 'error', buttons: false, timer: 3000 })
        this.$router.replace({ name: 'Lista Plantillas' })
      } finally {
        this.loading = false
      }
    },

    autoKey (item, source) {
      if (item._autoKey && source) item.key = slugify(source)
    },

    toggleSection (index) {
      this.$set(this.expandedSections, index, !this.expandedSections[index])
    },
    isSectionExpanded (index) {
      return !!this.expandedSections[index]
    },

    selectField (sIndex, gIndex, fIndex) {
      this.selectedField = { sIndex, gIndex, fIndex }
    },
    closeProperties () {
      this.selectedField = null
    },
    isFieldSelected (sIndex, gIndex, fIndex) {
      if (!this.selectedField) return false
      const s = this.selectedField
      return s.sIndex === sIndex && s.gIndex === gIndex && s.fIndex === fIndex
    },
    deleteSelectedField () {
      if (!this.selectedField) return
      const { sIndex, gIndex, fIndex } = this.selectedField
      const group = this.definition[sIndex]?.groups[gIndex]
      if (group) {
        group.fields.splice(fIndex, 1)
        this.selectedField = null
      }
    },

    fieldTypeIcon (type) {
      const m = FIELD_TYPES.find(t => t.value === type)
      return m ? m.icon : 'mdi-form-textbox'
    },
    fieldTypeName (type) {
      const m = FIELD_TYPES.find(t => t.value === type)
      return m ? m.text : type
    },

    scrollToSection (sIndex) {
      this.activeView = 'structure'
      this.$set(this.expandedSections, sIndex, true)
      this.$nextTick(() => {
        const el = this.$el.querySelector(`[data-section="${sIndex}"]`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },

    editSectionInline (sIndex) {
      this.editingSection = this.editingSection === sIndex ? null : sIndex
    },
    editGroupInline (sIndex, gIndex) {
      const key = sIndex + '-' + gIndex
      this.editingGroup = this.editingGroup === key ? null : key
    },

    addSection () {
      this.definition.push(emptySection())
      const idx = this.definition.length - 1
      this.$set(this.expandedSections, idx, true)
    },
    removeSection (index) {
      this.definition.splice(index, 1)
      this.selectedField = null
      this.editingSection = null
    },
    addGroup (section) { section.groups.push(emptyGroup()) },
    removeGroup (section, gIndex) {
      section.groups.splice(gIndex, 1)
      this.selectedField = null
      this.editingGroup = null
    },
    addField (group) { group.fields.push(emptyField()) },
    removeField (group, fIndex) {
      group.fields.splice(fIndex, 1)
      this.selectedField = null
    },

    addOption (field) {
      if (!field.options) this.$set(field, 'options', [])
      field.options.push({ value: '', label: '' })
    },
    removeOption (field, oIndex) { field.options.splice(oIndex, 1) },
    addTableRow (field) {
      if (!field.rows) this.$set(field, 'rows', [])
      field.rows.push({ key: '', label: '' })
    },
    removeTableRow (field, rIndex) { field.rows.splice(rIndex, 1) },
    addSubField (field) {
      if (!field.fields) this.$set(field, 'fields', [])
      field.fields.push(emptyField())
    },
    removeSubField (field, sfIndex) { field.fields.splice(sfIndex, 1) },

    needsOptions (type) { return ['SELECT', 'CHOICE', 'MULTI_CHOICE'].includes(type) },
    isTableType (type) { return type === 'TABLE' },
    isListType (type) { return type === 'LIST' },

    buildPayload () {
      const uniqueKey = (key, fallback, used) => {
        let k = key || fallback
        if (!k) k = fallback
        let candidate = k
        let n = 1
        while (used.has(candidate)) candidate = `${k}_${++n}`
        used.add(candidate)
        return candidate
      }
      const cleanFields = (fields) => {
        const usedKeys = new Set()
        return fields.map((f, i) => {
          const key = uniqueKey(f.key, slugify(f.label) || `field_${i + 1}`, usedKeys)
          const field = { key, label: f.label, type: f.type, width: f.width }
          if (f.required) field.required = true
          if (f.help) field.help = f.help
          if (f.prefill) field.prefill = f.prefill
          if (['SELECT', 'CHOICE', 'MULTI_CHOICE'].includes(f.type) && f.options?.length) {
            field.options = f.options.filter(o => o.value || o.label)
          }
          if (f.type === 'TABLE') {
            if (f.rows?.length) field.rows = f.rows.filter(r => r.key || r.label)
            if (f.fields?.length) field.fields = cleanFields(f.fields)
          }
          if (f.type === 'LIST') {
            if (f.fields?.length) field.fields = cleanFields(f.fields)
            if (f.itemLabel) field.itemLabel = f.itemLabel
            if (f.minItems) field.minItems = f.minItems
            if (f.maxItems) field.maxItems = f.maxItems
          }
          if (f.type === 'TEXTAREA' && f.minHeight) field.minHeight = f.minHeight
          if (f.type === 'MONEY' && f.hideCurrencySymbol) field.hideCurrencySymbol = true
          if (f.type === 'FILES' && f.maxFiles) field.maxFiles = f.maxFiles
          return field
        })
      }
      const sectionKeys = new Set()
      return {
        name: this.name,
        description: this.description,
        displayOrder: this.displayOrder,
        definition: this.definition.map((s, si) => {
          const groupKeys = new Set()
          const section = {
            key: uniqueKey(s.key, slugify(s.title) || `section_${si + 1}`, sectionKeys),
            title: s.title,
            groups: s.groups.map((g, gi) => ({
              key: uniqueKey(g.key, slugify(g.title) || `group_${gi + 1}`, groupKeys),
              title: g.title,
              ...(g.description ? { description: g.description } : {}),
              fields: cleanFields(g.fields),
            })),
          }
          if (s.newPage) section.newPage = true
          if (s.reusable) section.reusable = true
          return section
        }),
      }
    },

    async save () {
      if (this.isReadonly) return
      if (!this.name.trim()) {
        this.$swal({ text: 'El nombre de la plantilla es obligatorio.', icon: 'warning', buttons: false, timer: 3000 })
        this.activeView = 'info'
        return
      }
      try {
        this.saving = true
        const payload = this.buildPayload()
        if (this.isNew) {
          const created = await createFormTemplate(payload)
          this.templateId = created.id
          this.isNew = false
          this.status = created.status
          this.version = created.version
          if (this.linkedServiceIds.length) {
            await updateTemplateServices(this.templateId, this.linkedServiceIds)
          }
          this.$swal({ text: 'Plantilla creada correctamente.', icon: 'success', buttons: false, timer: 3000 })
          this.$router.replace({ name: 'Editor Plantilla', params: { id: this.templateId } })
        } else {
          await updateFormTemplate(this.templateId, payload)
          await updateTemplateServices(this.templateId, this.linkedServiceIds)
          this.$swal({ text: 'Plantilla guardada.', icon: 'success', buttons: false, timer: 3000 })
          await this.loadTemplate()
        }
      } catch (err) {
        console.error('Error saving template:', err)
        this.$swal({ text: err?.message || 'Error al guardar la plantilla.', icon: 'error', buttons: false, timer: 4000 })
      } finally {
        this.saving = false
      }
    },

    async publishTemplate () {
      if (this.isNew) {
        this.$swal({ text: 'Primero guarda la plantilla.', icon: 'warning', buttons: false, timer: 3000 })
        return
      }
      const confirmed = await this.$swal({
        title: '¿Publicar esta plantilla?',
        text: 'Al publicar, esta plantilla se usara en los nuevos procesos. Si hay otra publicada para los mismos servicios, sera archivada.',
        icon: 'warning',
        buttons: ['Cancelar', 'Publicar'],
      })
      if (!confirmed) return
      try {
        await this.save()
        await publishFormTemplate(this.templateId)
        this.$swal({ text: 'Plantilla publicada.', icon: 'success', buttons: false, timer: 3000 })
        await this.loadTemplate()
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Error al publicar.'
        this.$swal({ text: msg, icon: 'error', buttons: false, timer: 4000 })
      }
    },

    adjustEditorHeight () {
      const main = document.querySelector('.v-main')
      if (main && this.$el) {
        const pt = parseInt(getComputedStyle(main).paddingTop) || 64
        this.$el.style.height = `calc(100vh - ${pt}px)`
      }
    },

    previewCols (field) {
      if (field.width === 'half') return { cols: 12, md: 6 }
      if (field.width === 'third') return { cols: 12, md: 4 }
      return { cols: 12 }
    },

    async republish () {
      const confirmed = await this.$swal({
        title: '¿Republicar esta plantilla?',
        text: 'Se publicara de nuevo. Si hay otra publicada para los mismos servicios, sera archivada.',
        icon: 'warning',
        buttons: ['Cancelar', 'Republicar'],
      })
      if (!confirmed) return
      try {
        await publishFormTemplate(this.templateId)
        this.$swal({ text: 'Plantilla republicada.', icon: 'success', buttons: false, timer: 3000 })
        await this.loadTemplate()
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Error al republicar.'
        this.$swal({ text: msg, icon: 'error', buttons: false, timer: 4000 })
      }
    },

    async duplicateAndEdit () {
      try {
        const created = await duplicateFormTemplate(this.templateId)
        this.$router.replace({ name: 'Editor Plantilla', params: { id: created.id } })
        this.templateId = created.id
        this.isNew = false
        await this.loadTemplate()
      } catch (err) {
        this.$swal({ text: err?.message || 'Error al duplicar.', icon: 'error', buttons: false, timer: 3000 })
      }
    },

    goBack () {
      this.$router.push({ name: 'Lista Plantillas' })
    },
  },
}
</script>

<style>
.fte {
  background: #f5f6f8; margin: -12px;
  display: flex; flex-direction: column; overflow: hidden;
  height: calc(100vh - 76px);
}

.fte-header {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 20px; background: #fff; border-bottom: 1px solid #eef0f2;
  flex-shrink: 0;
}
.fte-header__info { flex: 1; margin-left: 4px; }
.fte-header__info h2 { font-size: 16px; font-weight: 700; margin: 0; line-height: 1.3; }

.fte-layout { display: flex; flex: 1; overflow: hidden; }

.fte-sidebar {
  width: 210px; flex-shrink: 0; background: #fff;
  border-right: 1px solid #eef0f2; display: flex; flex-direction: column;
  overflow-y: auto;
}
.fte-sidebar__item {
  display: flex; align-items: center; padding: 11px 18px; cursor: pointer;
  font-size: 13px; color: #636569; transition: background 0.15s;
}
.fte-sidebar__item:hover { background: #f5f6f8; }
.fte-sidebar__item--active {
  color: #fc5b00; background: #fff5f0; font-weight: 600;
  border-right: 3px solid #fc5b00;
}
.fte-sidebar__divider { height: 1px; background: #eef0f2; margin: 6px 0; }
.fte-sidebar__label {
  font-size: 11px; font-weight: 700; color: #9e9e9e;
  text-transform: uppercase; letter-spacing: 0.05em; padding: 12px 18px 6px;
}
.fte-sidebar__section-link {
  padding: 6px 18px 6px 24px; font-size: 13px; color: #636569; cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fte-sidebar__section-link:hover { color: #fc5b00; }
.fte-sidebar__tip {
  margin: auto 14px 14px; padding: 14px; background: #fff5f0;
  border-radius: 10px; font-size: 12px; line-height: 1.5;
}

.fte-content { flex: 1; padding: 24px 28px; overflow-y: auto; }
.fte-content__inner { max-width: 960px; }

.fte-stats { display: flex; gap: 14px; margin-bottom: 20px; }
.fte-stats span { font-size: 13px; color: #9e9e9e; }
.fte-stats strong { color: #636569; }

.fte-section-card {
  background: #fff; border: 1px solid #eef0f2; border-radius: 12px;
  margin-bottom: 16px; overflow: hidden;
}
.fte-section-header {
  display: flex; align-items: center; padding: 14px 16px; cursor: pointer;
  transition: background 0.12s;
}
.fte-section-header:hover { background: #fafbfc; }

.fte-group-label {
  display: flex; align-items: center; padding: 0 16px; margin-bottom: 8px;
}
.fte-group-title {
  font-size: 11px; font-weight: 700; color: #9e9e9e;
  text-transform: uppercase; letter-spacing: 0.06em; flex: 1;
}

.fte-field {
  display: flex; align-items: center; padding: 10px 14px;
  background: #fff; border: 1.5px solid #eef0f2; border-radius: 10px;
  margin: 0 16px 6px; cursor: pointer; transition: all 0.12s;
}
.fte-field:hover { border-color: #d0d2d5; }
.fte-field--selected { border-color: #fc5b00 !important; background: #fffaf7; }
.fte-field__icon {
  width: 34px; height: 34px; border-radius: 8px; background: #f0f1f3;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.fte-add-btn {
  display: flex; align-items: center; justify-content: center;
  padding: 10px; margin: 4px 16px 0; border: 2px dashed #e3e5e8;
  border-radius: 10px; cursor: pointer; transition: all 0.12s;
}
.fte-add-btn:hover { border-color: #fc5b00; background: #fffaf7; }
.fte-add-section {
  display: flex; align-items: center; justify-content: center;
  padding: 18px; border: 2px dashed #e3e5e8; border-radius: 12px;
  cursor: pointer; transition: all 0.12s; margin-top: 4px;
}
.fte-add-section:hover { border-color: #fc5b00; background: #fffaf7; }

.fte-properties {
  width: 300px; flex-shrink: 0; background: #fff;
  border-left: 1px solid #eef0f2; padding: 20px; overflow-y: auto;
}
.fte-prop-label { font-size: 12px; font-weight: 600; color: #636569; margin-bottom: 4px; }
.fte-prop-card {
  padding: 12px 14px; border: 1px solid #eef0f2; border-radius: 10px;
}
.fte-delete-btn {
  display: flex; align-items: center; justify-content: center;
  padding: 10px; border: 1.5px solid #ffcdd2; border-radius: 10px;
  cursor: pointer; transition: background 0.12s;
}
.fte-delete-btn:hover { background: #ffebee; }

.fte-empty {
  text-align: center; padding: 48px 24px;
  border: 2px dashed #e3e5e8; border-radius: 12px;
}

.fte-readonly-bar {
  display: flex; align-items: center; justify-content: center;
  padding: 8px 20px; background: #455a64; color: #fff;
  font-size: 13px; flex-shrink: 0;
}

.fte-preview { max-width: 800px; }
.fte-preview-section {
  background: #fff; border: 1px solid #eef0f2; border-radius: 12px;
  margin-bottom: 20px; overflow: hidden;
}
.fte-preview-section__title {
  font-size: 15px; font-weight: 700; padding: 14px 20px;
  background: #fafbfc; border-bottom: 1px solid #eef0f2;
}
.fte-preview-group {
  padding: 16px 20px 4px;
}
.fte-preview-group__title {
  font-size: 12px; font-weight: 700; color: #9e9e9e;
  text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 12px;
}
.fte-preview-placeholder {
  display: flex; align-items: center; justify-content: center;
  height: 80px; border: 1.5px dashed #e3e5e8; border-radius: 8px;
  color: #bdbdbd;
}
</style>
